import React, { memo, useCallback, useState } from 'react'

import { Button, CircleCheck, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import Modal from 'Pods/Shared/Modal/Modal'
import AccessCodeForm from 'Pods/Shared/Waitlist/AccessCodeForm/AccessCodeForm'
import UpgradeCard from 'Pods/Shared/Waitlist/UpgradeCard/UpgradeCard'
import WaitlistCard from 'Pods/Shared/Waitlist/WaitlistCard/WaitlistCard'
import { STUDIO_RETURN_URL, TODO_LEARN_MORE_HREF } from 'Pods/ThreeDStudio/Components/UsageModal/constants'

const ICON_SIZE = { height: '20px', width: '20px' }

// The landing has no 3D Studio store, so the subscription values shown in the upsell are literals
// (the hero card uses the same numbers).
const SUBSCRIPTION_PRICE = '$9.99'
const SUBSCRIPTION_ORIGINAL_PRICE = '$24.99'
const SUBSCRIPTION_MONTHLY_MODELS = 25

// A valid access code grants immediate in_free_beta access (AccessCodesController#create). The
// landing has no generator to use it, so send the user to the studio. Delay the redirect so the
// success toast AccessCodeForm fires right after onApplied stays visible before we navigate away.
const REDIRECT_DELAY_MS = 2000

// The must_join_waitlist hero CTA opens this modal. It reuses the shared free-beta WaitlistCard
// (the "left side" of the 3D Studio usage modal). Joining can't flip the tier the way the studio
// modal does (no store), so the joined state is lifted to UpgradeHeader: onJoined notifies the
// parent, which switches the hero card to the waitlisted layout and feeds isJoined back here to
// swap the body to the "You're on the waitlist!" confirmation -- the same layout as the studio
// WaitlistedBody, including the subscription upsell.
function WaitlistModal({ isOpen, isJoined, onClose: handleClose, onJoined, subscriptionHref }) {
  const [isCodeFormOpen, setIsCodeFormOpen] = useState(false)

  const handleToggleCodeForm = useCallback(() => {
    setIsCodeFormOpen(prev => !prev)
  }, [])

  const handleCodeApplied = useCallback(() => {
    window.setTimeout(() => {
      window.location.assign(STUDIO_RETURN_URL)
    }, REDIRECT_DELAY_MS)
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="small"
      header={isJoined ? 'Beta access requested' : 'Unlock AI Generation'}
      customClass="usage-modal usage-modal--ai-sub-landing"
      ariaLabel={isJoined ? 'Beta access requested' : 'Unlock generation'}
    >
      {isJoined ? (
        <div className="usage-modal__waitlisted">
          <div className="usage-modal__waitlisted-headline">
            <Icon svgComponent={CircleCheck} color="positive" size={ICON_SIZE} notSpaced />
            <p className="cgt-heading-200">You&apos;re on the waitlist!</p>
          </div>
          <p className="usage-modal__waitlisted-copy cgt-body-md">
            We&apos;ll review your request and notify you by email once access is available.
          </p>

          <Button
            size="small"
            type="secondary"
            customClass="usage-modal__waitlisted-code-toggle"
            onClick={handleToggleCodeForm}
          >
            I have access code
          </Button>
          {isCodeFormOpen && <AccessCodeForm onApplied={handleCodeApplied} onClose={handleClose} />}

          <UpgradeCard
            title="Faster access with Subscription"
            price={SUBSCRIPTION_PRICE}
            originalPrice={SUBSCRIPTION_ORIGINAL_PRICE}
            monthlyModels={SUBSCRIPTION_MONTHLY_MODELS}
            subscriptionHref={subscriptionHref}
            learnMoreHref={TODO_LEARN_MORE_HREF}
            onClose={handleClose}
          />
        </div>
      ) : (
        <WaitlistCard onJoined={onJoined} onCodeApplied={handleCodeApplied} onClose={handleClose} />
      )}
    </Modal>
  )
}

WaitlistModal.propTypes = {
  isOpen: bool.isRequired,
  isJoined: bool.isRequired,
  onClose: func.isRequired,
  onJoined: func.isRequired,
  subscriptionHref: string.isRequired,
}

export default memo(WaitlistModal)
